/**
 * Color Blindness Simulation Utilities
 *
 * Uses the Machado et al. (2009) physiologically-based model.
 * Matrices operate in linearized sRGB space (gamma correction applied).
 * These are the same matrices used by Chrome DevTools.
 */

export type SimulationType =
  | 'normal'
  | 'protanopia'
  | 'deuteranopia'
  | 'tritanopia'
  | 'protanomaly'
  | 'deuteranomaly'
  | 'achromatopsia'

export interface SimulationInfo {
  id: SimulationType
  label: string
  description: string
  prevalence: string
  affectedColors: string
}

export const SIMULATION_TYPES: SimulationInfo[] = [
  {
    id: 'normal',
    label: 'Normal Vision',
    description: 'Standard trichromatic color vision with all three cone types functioning.',
    prevalence: '~95% of population',
    affectedColors: 'None'
  },
  {
    id: 'deuteranomaly',
    label: 'Deuteranomaly',
    description: 'Reduced sensitivity to green light. Most common form of color blindness.',
    prevalence: '~5% of men, 0.4% of women',
    affectedColors: 'Green, yellow, red appear similar'
  },
  {
    id: 'protanomaly',
    label: 'Protanomaly',
    description: 'Reduced sensitivity to red light. Reds appear darker and less saturated.',
    prevalence: '~1% of men, 0.01% of women',
    affectedColors: 'Red appears darker, green-red confusion'
  },
  {
    id: 'deuteranopia',
    label: 'Deuteranopia',
    description: 'Complete absence of green-sensitive cones (M-cones).',
    prevalence: '~1% of men, 0.01% of women',
    affectedColors: 'Cannot distinguish red from green'
  },
  {
    id: 'protanopia',
    label: 'Protanopia',
    description: 'Complete absence of red-sensitive cones (L-cones).',
    prevalence: '~1% of men, 0.01% of women',
    affectedColors: 'Cannot distinguish red from green; red appears black'
  },
  {
    id: 'tritanopia',
    label: 'Tritanopia',
    description: 'Complete absence of blue-sensitive cones (S-cones). Very rare.',
    prevalence: '~0.003% of all people',
    affectedColors: 'Cannot distinguish blue from green, yellow from violet'
  },
  {
    id: 'achromatopsia',
    label: 'Achromatopsia',
    description: 'Complete color blindness. Only brightness differences are perceived.',
    prevalence: '~0.003% of all people',
    affectedColors: 'All colors appear as shades of grey'
  }
]

// 3x3 matrices for dichromacy simulation (Machado et al. 2009)
// Applied to linearized sRGB values [R, G, B]
const DICHROMACY_MATRICES: Record<string, number[][]> = {
  protanopia: [
    [0.152286, 1.052583, -0.204868],
    [0.114475, 0.885368, 0.000157],
    [-0.003711, -0.004391, 1.008102]
  ],
  deuteranopia: [
    [0.367322, 0.860646, -0.227968],
    [0.280085, 0.672501, 0.047413],
    [-0.011820, 0.042940, 0.968881]
  ],
  tritanopia: [
    [1.255528, -0.076749, -0.178779],
    [-0.078411, 0.930809, 0.147602],
    [0.004733, 0.691367, 0.303900]
  ]
}

// Identity matrix (normal vision)
const IDENTITY: number[][] = [
  [1, 0, 0],
  [0, 1, 0],
  [0, 0, 1]
]

// Interpolate between identity and dichromacy matrix for anomaly simulation
function interpolateMatrix(base: number[][], severity: number): number[][] {
  return base.map((row, i) =>
    row.map((val, j) => IDENTITY[i][j] + severity * (val - IDENTITY[i][j]))
  )
}

// Convert sRGB [0-255] to linear light
function toLinear(value: number): number {
  const v = value / 255
  return v <= 0.04045 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4)
}

// Convert linear light back to sRGB [0-255]
function toSRGB(value: number): number {
  const v = value <= 0.0031308 ? 12.92 * value : 1.055 * Math.pow(value, 1 / 2.4) - 0.055
  return Math.round(Math.min(255, Math.max(0, v * 255)))
}

// Apply a 3x3 matrix to [R, G, B] in linear space
function applyMatrix(matrix: number[][], r: number, g: number, b: number): [number, number, number] {
  return [
    matrix[0][0] * r + matrix[0][1] * g + matrix[0][2] * b,
    matrix[1][0] * r + matrix[1][1] * g + matrix[1][2] * b,
    matrix[2][0] * r + matrix[2][1] * g + matrix[2][2] * b
  ]
}

export function applyColorBlindnessFilter(
  imageData: ImageData,
  type: SimulationType
): ImageData {
  const data = new Uint8ClampedArray(imageData.data)
  const len = data.length

  if (type === 'normal') {
    return new ImageData(data, imageData.width, imageData.height)
  }

  if (type === 'achromatopsia') {
    // Standard luminance formula
    for (let i = 0; i < len; i += 4) {
      const rLin = toLinear(data[i])
      const gLin = toLinear(data[i + 1])
      const bLin = toLinear(data[i + 2])
      const lum = 0.2126 * rLin + 0.7152 * gLin + 0.0722 * bLin
      const grey = toSRGB(lum)
      data[i] = grey
      data[i + 1] = grey
      data[i + 2] = grey
      // data[i + 3] = alpha unchanged
    }
    return new ImageData(data, imageData.width, imageData.height)
  }

  // Choose base matrix and severity
  let baseMatrixKey: string
  let severity: number

  switch (type) {
    case 'protanopia':
      baseMatrixKey = 'protanopia'
      severity = 1.0
      break
    case 'deuteranopia':
      baseMatrixKey = 'deuteranopia'
      severity = 1.0
      break
    case 'tritanopia':
      baseMatrixKey = 'tritanopia'
      severity = 1.0
      break
    case 'protanomaly':
      baseMatrixKey = 'protanopia'
      severity = 0.6
      break
    case 'deuteranomaly':
      baseMatrixKey = 'deuteranopia'
      severity = 0.6
      break
    default:
      return new ImageData(data, imageData.width, imageData.height)
  }

  const matrix = severity === 1.0
    ? DICHROMACY_MATRICES[baseMatrixKey]
    : interpolateMatrix(DICHROMACY_MATRICES[baseMatrixKey], severity)

  for (let i = 0; i < len; i += 4) {
    const rLin = toLinear(data[i])
    const gLin = toLinear(data[i + 1])
    const bLin = toLinear(data[i + 2])

    const [rOut, gOut, bOut] = applyMatrix(matrix, rLin, gLin, bLin)

    data[i] = toSRGB(rOut)
    data[i + 1] = toSRGB(gOut)
    data[i + 2] = toSRGB(bOut)
    // alpha unchanged
  }

  return new ImageData(data, imageData.width, imageData.height)
}

export function processImageWithFilter(
  canvas: HTMLCanvasElement,
  imageData: ImageData,
  type: SimulationType
): string {
  const ctx = canvas.getContext('2d')!
  const filtered = applyColorBlindnessFilter(imageData, type)
  ctx.putImageData(filtered, 0, 0)
  return canvas.toDataURL('image/png')
}
